import React, { FunctionComponent, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useIntl } from 'react-intl'
import { View } from 'react-native'
import { useSelector } from 'react-redux'
import { useLocation, useNavigate, useOutletContext } from 'react-router'

import { skipToken } from '@reduxjs/toolkit/query'
import * as Sentry from '@sentry/react'
import { FormApi } from 'final-form'
import { cloneDeep, get, isEmpty } from 'lodash-es'
import styled, { useTheme } from 'styled-components/native'

import {
  dsmFifthEditionDiagnoses,
  TREATMENT_FOCUS_TITLES,
} from '@lyrahealth-inc/lt-metadata/BCActivities/Notes/common/messages'
import { AlertBannerTypes } from '@lyrahealth-inc/shared-app-logic/src/features/alerts/AlertBannerTypes'
import { getDateFromAppointment } from '@lyrahealth-inc/shared-app-logic/src/features/appointments/getDateFromAppointment'
import {
  getFollowingAppointment,
  isSessionOlderThan90Days,
} from '@lyrahealth-inc/shared-app-logic/src/features/appointments/utils'
import {
  getAIEnabledFields,
  getClientPreferredNameIfExists,
} from '@lyrahealth-inc/shared-app-logic/src/features/assignments/utils'
import { STATES } from '@lyrahealth-inc/shared-app-logic/src/features/common/constants/constants'
import { S4C_PROPERTIES } from '@lyrahealth-inc/shared-app-logic/src/features/common/constants/mixpanelConstants'
import { TREATMENT_OPTIONS_TYPE } from '@lyrahealth-inc/shared-app-logic/src/features/common/constants/treatmentOptions'
import { useIsMounted } from '@lyrahealth-inc/shared-app-logic/src/features/common/hooks/useIsMounted'
import { useThrottledCallback } from '@lyrahealth-inc/shared-app-logic/src/features/common/hooks/useThrottledCallback'
import {
  extendContentMetadata,
  removeContentMetadataField,
} from '@lyrahealth-inc/shared-app-logic/src/features/common/utils/contentSchemaUtils'
import {
  getSessionLimitProgramCategoryByTreatmentOption,
  isUnlimitedSessionLimit,
} from '@lyrahealth-inc/shared-app-logic/src/features/common/utils/sessionLimit'
import { useFlags } from '@lyrahealth-inc/shared-app-logic/src/features/flags/useFlags'
import { PROGRAMS } from '@lyrahealth-inc/shared-app-logic/src/features/programConfigs/programConfigs'
import {
  getProgramNameFromId,
  isNeuropsychEvaluationProgram,
} from '@lyrahealth-inc/shared-app-logic/src/features/programConfigs/programHelpers'
import { useGetCustomerForClient } from '@lyrahealth-inc/shared-app-logic/src/features/providers/hooks/useGetCustomerForClient'
import {
  programSupportForAttribute,
  resolveSessionLimit,
} from '@lyrahealth-inc/shared-app-logic/src/features/sessions/utils'
import {
  AIDraftStatus,
  AppointmentSummary,
  noteSummaryStatusText,
} from '@lyrahealth-inc/shared-app-logic/src/models/appointmentSummary/AppointmentSummary'
import { Episode } from '@lyrahealth-inc/shared-app-logic/src/models/episodes/Episodes'
import { SchemaProperties } from '@lyrahealth-inc/shared-app-logic/src/models/formMetadata/FormMetadata'
import { Note, NoteStatusStates, NoteTypes } from '@lyrahealth-inc/shared-app-logic/src/models/notes/Notes'
import { LTProgramName, Program, ProgramNames } from '@lyrahealth-inc/shared-app-logic/src/models/programs/Programs'
import { BodyText } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/bodyText/BodyText'
import { AttentionIcon } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/icons/AttentionIcon'
import { ChevronIcon, ChevronIconDirection } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/icons/ChevronIcon'
import { LoadingIndicator } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/icons/LoadingIndicator'
import { Link } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/link/Link'
import { PrimaryButton } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/primaryButton/PrimaryButton'
import { AlertBanner } from '@lyrahealth-inc/ui-core-crossplatform/src/molecules/alertBanner/AlertBanner'
import { EligibilityErrorBanner } from '@lyrahealth-inc/ui-core-crossplatform/src/molecules/eligibilityErrorBanner/EligibilityErrorBanner'
import { ExpandingSidePanel } from '@lyrahealth-inc/ui-core-crossplatform/src/molecules/expandingSidePanel/ExpandingSidePanel'
import { InlineInfoBanner } from '@lyrahealth-inc/ui-core-crossplatform/src/molecules/inlineInfoBanner/InlineInfoBanner'
import { InlineWarningBanner } from '@lyrahealth-inc/ui-core-crossplatform/src/molecules/inlineWarningBanner/InlineWarningBanner'
import { TabbedExpandingSidePanel } from '@lyrahealth-inc/ui-core-crossplatform/src/molecules/sidePanel/TabbedExpandingSidePanel'
import { ClientDetailsHeader } from '@lyrahealth-inc/ui-core-crossplatform/src/organisms/clientDetailsHeader/ClientDetailsHeader'
import { FormButtonParams } from '@lyrahealth-inc/ui-core-crossplatform/src/organisms/formBody/FormBody'
import { NoteContainer } from '@lyrahealth-inc/ui-core-crossplatform/src/organisms/notes/NoteContainer'
import { NotesSidebar } from '@lyrahealth-inc/ui-core-crossplatform/src/organisms/notes/NotesSidebar'
import { BodyTextSize } from '@lyrahealth-inc/ui-core-crossplatform/src/styles/typeStyles'
import { ThemeType } from '@lyrahealth-inc/ui-core-crossplatform/src/utils/themes/ThemeProvider'
import { tID } from '@lyrahealth-inc/ui-core-crossplatform/src/utils/utils'

import ClientNotesPanel from './ClientNotesPanel'
import { setToastContent } from '../data/ltToastAutoActions'
import { useCreateNoteMutation, useGetAppointmentSummariesQuery, useUpdateNoteMutation } from './data/notesApi'
import { useAppointmentChargeCheck } from './hooks/useAppointmentChargeCheck'
import { useFlexibleNoteChargeSubmittedCheck } from './hooks/useFlexibleNoteChargeSubmittedCheck'
import { FLEX_NOTE_RATE_FIELDS, useFlexibleNoteCloseSession } from './hooks/useFlexibleNoteCloseSession'
import { useFlexibleNoteMetadata } from './hooks/useFlexibleNoteMetadata'
import {
  PANEL_TAB_IDS,
  SIDEBAR_TRANSITION_TIME,
  useGetTabbedExpandingSidePanelContent,
} from './hooks/useGetTabbedExpandingSidePanelContent'
import { useLoadPreviousFlexibleNote } from './hooks/useLoadPreviousFlexibleNote'
import { useReversedChargeCheck } from './hooks/useReversedChargeCheck'
import {
  ASYNC_TIME_NOTE_NAME,
  getAsyncTimeNoteExternalValues,
  isEpisodeClosedForAsyncTime,
} from './utils/asyncTimeNote'
import { DA_FLEXIBLE_NOTE_NAME, PROGRAM_TO_THERAPY_TYPE } from './utils/noteConstants'
import { getOriginRouteDestination } from './utils/noteNavigationUtils'
import {
  getNpSessionNoteExternalValues,
  hasNpTemplateQuestions,
  NP_SESSION_NOTE_NAME,
  NP_SESSION_TYPES_WITH_FORM,
} from './utils/npSessionNote'
import {
  DA_FLEXIBLE_NOTE_REVERSED_CHARGE_EDITABLE_FIELDS,
  getReversedChargeNoteMetadata,
  isDASessionNoteType,
  scrubFlexibleNotePII,
} from './utils/reversedChargeNoteUtils'
import { getSeededFlexibleNoteBody } from './utils/seedFlexibleNoteBody'
import { actions, mixpanelEvents, sessionNoteTypes } from '../../../../mixpanel/mixpanelConstants'
import { track } from '../../../../mixpanel/mixpanelTracking'
import { programConfig, ROLES, SUPERVISOR_ROLES } from '../../common/constants/appConstants'
import {
  CLIENT_END_OF_SESSION,
  CLIENT_HOME,
  CLIENT_NOTES,
  CLIENTS_SESSIONS,
  DASHBOARD,
  LC_CALENDAR_CURRENT,
  LT_CLIENTS,
} from '../../common/constants/routingConstants'
import { hasRole, logToSentry } from '../../common/utils/utils'
import {
  getAuthRoles,
  getAuthSupervisor,
  getAuthUserAccessType,
  getAuthUserFullName,
  getAuthUserId,
} from '../../data/auth/authSelectors'
import { getHealthPlanICD10s } from '../../data/healthPlan/healthPlanSelectors'
import { getSelectedClientId } from '../../data/lyraTherapy/clientSelectors'
import { getLyraTherapyContentsData, getLyraTherapyContentsPrograms } from '../../data/lyraTherapy/contentSelectors'
import { getRequestPaymentCustomers } from '../../data/requestPayment/requestPaymentSelectors'
import { useAppDispatch } from '../../data/store'
import { ACTIVE_ALERT_HEIGHT, JOIN_BANNER_HEIGHT, useAllBannerVisibility } from '../../hooks/useAllBannerVisibility'
import { useIsEngageDAProvider } from '../../hooks/useIsEngageDAProvider'
import { usePreviousRoute } from '../../hooks/usePreviousRoute'
import { useVideoSessionBannerHeight } from '../../hooks/useVideoSessionBannerHeight'
import { useUpdateTransferMutation } from '../../payments/data/transfersApi'
import { useClientAppointmentsData } from '../clients/clientDetails/data/hooks/useClientAppointmentsData'
import { useCustomersForClient } from '../clients/clientDetails/data/hooks/useCustomersForClient'
import { useSelectedClientData } from '../clients/clientDetails/data/hooks/useSelectedClientData'
import { useProgramCoverageBreakdown } from '../clients/clientDetails/hooks/useProgramCoverageBreakdown'
import { useSessionLimitInfo } from '../clients/clientDetails/hooks/useSessionLimitInfo'
import { SessionLimitWarnings } from '../clients/clientDetails/SessionLimitWarnings'

const NOTE_DETAILS_HEADER_HEIGHT = 114
const ALERT_BANNER_HEIGHT = 100

const ContentContainer = styled.View<{ offset: number }>(({ offset }) => {
  return {
    position: 'relative',
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'flex-start',
    height: `calc(100% - ${offset}px)`,
    width: '100%',
  }
})

const SubmissionAlertContainer = styled.View<{ topOffset: number }>(({ topOffset }) => ({
  position: 'sticky',
  top: `${topOffset}px`,
  zIndex: 10,
  width: '100%',
}))

const MainSectionContainer = styled.View({
  flexDirection: 'row',
  justifyContent: 'space-between',
  alignItems: 'flex-start',
  width: '100%',
})

const WholeContainer = styled.View({
  flexDirection: 'row',
  flexGrow: 1,
})

const LeftSideContainer = styled.View({
  flexGrow: 1,
})

const RightSideContainer = styled.View<{
  offset: number
}>(({ offset }) => {
  return {
    position: 'sticky',
    top: '0px',
    height: `calc(100vh - ${offset}px)`,
    alignSelf: 'flex-start',
    zIndex: 10,
  }
})

const SidebarContainer = styled.View<{
  theme: ThemeType
  topOffset: number
}>(({ theme: { spacing }, topOffset }: { theme: ThemeType; topOffset: number }) => ({
  position: 'sticky',
  top: `${topOffset}px`,
  margin: `0 ${spacing['16px']}`,
}))

const GoBackContainer = styled.Pressable<{ theme: ThemeType }>(({ theme: { spacing } }) => ({
  flexDirection: 'row',
  alignItems: 'center',
  marginTop: spacing['32px'],
}))

const NoteLayoutContainer = styled.View<{ theme: ThemeType }>(({ theme: { spacing } }) => ({
  width: '690px',
  height: '100%',
  margin: 0,
  marginTop: spacing['32px'],
  marginBottom: spacing['32px'],
  paddingLeft: spacing['32px'],
  paddingTop: 0,
}))

const StickyHeaderContainer = styled.View<{ theme: ThemeType }>(({ theme: { colors } }) => ({
  position: 'sticky',
  top: 0,
  zIndex: 11,
  backgroundColor: colors.backgroundPrimary,
}))

const CenteredContentContainer = styled.View({
  flexDirection: 'row',
  alignItems: 'flex-start',
  flex: 1,
})

const ContentGroup = styled.View({
  flexDirection: 'row',
  alignItems: 'flex-start',
})

const FlexSpacer = styled.View<{ grow: number; transitionTime: number }>(({ grow, transitionTime }) => ({
  flexGrow: grow,
  transition: `flex-grow ${transitionTime}ms ease-in-out`,
  minWidth: 0,
}))

const AINotesPanelContainer = styled.View<{ topOffset: number }>(({ topOffset }) => ({
  position: 'sticky',
  right: 0,
  top: `${topOffset}px`,
  height: `calc(100vh - ${topOffset}px)`,
  zIndex: 10,
}))

const ClientNoteDetails: FunctionComponent = () => {
  const {
    enableAINotesFeatures,
    enableDAFlexibleNote,
    showAINotesFeatures,
    showAINotesForLCMAndCLE,
    notesSidePanel,
    airlineCustomerValues,
    forceAdultFlowsToHPI,
    isNPTestingEnabled,
  } = useFlags()

  const noteRef = useRef<FormApi<Dict> | null>(null)
  const feedbackFormRef = useRef<FormApi<Dict>>()
  const userId = useSelector(getAuthUserId)
  const userName = useSelector(getAuthUserFullName)
  const roles = useSelector(getAuthRoles)
  const supervisor = useSelector(getAuthSupervisor)
  const accessType = useSelector(getAuthUserAccessType)
  const clientId = useSelector(getSelectedClientId)
  const { data: clientDetails } = useSelectedClientData()
  const clientCountryCustomer = useGetCustomerForClient(useCustomersForClient(), clientDetails)
  const {
    data: appointments = [],
    isLoading: isLoadingAppointments,
    isUninitialized: isAppointmentsUninitialized,
  } = useClientAppointmentsData()
  const icd10s = useSelector(getHealthPlanICD10s)
  const customers = useSelector(getRequestPaymentCustomers)
  const contents = useSelector(getLyraTherapyContentsData)
  const contentsPrograms = useSelector(getLyraTherapyContentsPrograms)
  const { episode } = useOutletContext<{ episode: Episode }>()
  const navigate = useNavigate()
  const location = useLocation()
  const intl = useIntl()
  const dispatch = useAppDispatch()
  const [createNote] = useCreateNoteMutation()
  const [updateNote] = useUpdateNoteMutation()
  const { colors } = useTheme()
  const { hasJoinSessionBannerOpen, hasActiveAlerts } = useAllBannerVisibility()
  const programCoverageBreakdown = useProgramCoverageBreakdown(clientDetails?.employer, clientDetails?.country)
  const sessionLimitInfo = useSessionLimitInfo({ clientDetails, programName: episode?.program_name })

  const [note, setNote] = useState<Note & { sessionDate: string; sessionNumber: string }>(() => {
    const noteFromState = get(location.state, 'note') || {}
    logToSentry('noteDetailsInit', {
      message: 'ClientNoteDetails initialized',
      hasLocationState: !!location.state,
      noteIdFromState: noteFromState?.id,
      appointmentIdFromState: noteFromState?.appointment_id,
      episodeId: episode?.id,
      clientId,
      pathname: location.pathname,
    })
    return cloneDeep(noteFromState)
  })

  const [isTabbedExpandingSidePanelOpen, setIsTabbedExpandingSidePanelOpen] = useState(true)

  // Charge id is set in state when navigation occurs from end of session -> session note for DA Engage Providers
  const chargeIdToUpdate = get(location.state, 'chargeId')
  const needToUpdateCharge = get(location.state, 'needToUpdateCharge') ?? false
  const originRoute = get(location.state, 'originRoute') as string | undefined
  const isFromRiskAlerts = originRoute === 'riskAlerts'
  const isFromClientList = originRoute === 'clientList'
  const isFromDashboardTasks = originRoute === 'tasks'
  const isFromCareTimeline = originRoute === 'careTimeline'
  const isFromClientOverview = originRoute === 'overview'

  const navigateToDashboard = () => {
    navigate(DASHBOARD.route)
  }

  const isEngageDAProvider = useIsEngageDAProvider()
  const [updateTransfer] = useUpdateTransferMutation()

  const isFlexibleNote =
    enableDAFlexibleNote && isEngageDAProvider && note?.content_meta_data?.name === DA_FLEXIBLE_NOTE_NAME

  const noteAppointment = useMemo(
    () => appointments.find(({ appointmentId }: any) => appointmentId === note.appointment_id),
    [appointments, note.appointment_id],
  )

  const {
    eligibility: closeSessionEligibility,
    closeSession,
    showHealthPlanIcd10,
    onValuesChange: onFlexNoteValuesChange,
    eligibilityError,
  } = useFlexibleNoteCloseSession({
    isFlexibleNote,
    appointmentToClose: noteAppointment,
    selectedEpisode: episode,
    savedNoteValues: note?.body,
  })

  const { isReversedChargeNote, isLoading: isLoadingReversedChargeCheck } = useReversedChargeCheck({
    noteName: note?.content_meta_data?.name,
    appointmentId: note?.appointment_id,
    clientId,
    providerId: userId,
  })

  const {
    hasSubmittedCharge: isChargeAlreadySubmitted,
    submittedChargeId,
    isLoading: isLoadingChargeSubmittedCheck,
  } = useFlexibleNoteChargeSubmittedCheck({
    noteName: note?.content_meta_data?.name,
    appointmentId: note?.appointment_id,
    clientId,
    providerId: userId,
  })

  const isLegacyDANote = isDASessionNoteType(note?.content_meta_data?.name)
  const isConcludedAppointment = noteAppointment?.appointmentStatus === 'concluded'
  const arrivedFromCloseSession = Boolean(chargeIdToUpdate)
  const isSessionCloseCheckEnabled =
    isEngageDAProvider &&
    isLegacyDANote &&
    isConcludedAppointment &&
    !arrivedFromCloseSession &&
    !noteAppointment?.isChargeReversed
  const { hasCharge, isLoading: isLoadingAppointmentChargeCheck } = useAppointmentChargeCheck({
    appointmentId: note?.appointment_id,
    clientId,
    providerId: userId,
    enabled: isSessionCloseCheckEnabled,
  })
  const sessionNeedsClosing = isSessionCloseCheckEnabled && !isLoadingAppointmentChargeCheck && !hasCharge

  const [isReversedChargeMetadataTransformed, setIsReversedChargeMetadataTransformed] = useState(false)
  const hasLockedReversedChargeFields = useRef(false)
  const sessionChargeIdRef = useRef<string | null>(null)
  const effectiveIsChargeAlreadySubmitted = isChargeAlreadySubmitted || !!sessionChargeIdRef.current
  const effectiveSubmittedChargeId = submittedChargeId ?? sessionChargeIdRef.current ?? undefined

  const { data: appointmentSummaries, isSuccess: hasFetchedSummaries } = useGetAppointmentSummariesQuery(
    clientId && episode?.id
      ? {
          providerId: userId,
          patientId: clientId,
          episodeId: episode?.id,
          statusOnly: false,
          appointmentId: note?.appointment_id ?? undefined,
        }
      : skipToken,
    { skip: !enableAINotesFeatures },
  )

  const noteIsCompleted = note.status === NoteStatusStates.COMPLETED
  const isAsyncTimeEntryLocked =
    note?.content_meta_data?.name === ASYNC_TIME_NOTE_NAME && isEpisodeClosedForAsyncTime(episode?.state)

  const hasRedirectedToCloseSession = useRef(false)
  useEffect(() => {
    if (!sessionNeedsClosing || noteIsCompleted || !noteAppointment || hasRedirectedToCloseSession.current) {
      return
    }
    hasRedirectedToCloseSession.current = true
    logToSentry('daNotesWorkflow', {
      message: 'DA notes workflow: draft legacy note has no charge, redirecting to close session',
      noteId: note?.id,
      appointmentId: note?.appointment_id,
      clientId,
    })
    navigate(CLIENT_END_OF_SESSION.route, {
      replace: true,
      state: { appointment: noteAppointment, draftNote: note, originRoute },
    })
  }, [sessionNeedsClosing, noteIsCompleted, noteAppointment, note, originRoute, clientId, navigate])

  const goToCloseSessionForCompletedNote = useCallback(() => {
    if (!noteAppointment) {
      return
    }
    logToSentry('daNotesWorkflow', {
      message: 'DA notes workflow: completed legacy note has no charge, provider chose to close the session',
      noteId: note?.id,
      appointmentId: note?.appointment_id,
      clientId,
    })
    navigate(CLIENT_END_OF_SESSION.route, {
      state: { appointment: noteAppointment, originRoute, noteAlreadyCompleted: true },
    })
  }, [noteAppointment, note?.id, note?.appointment_id, clientId, originRoute, navigate])

  let noteMetadata = note.content_meta_data
  const noteType = note.note_type

  const providerType = Object.values(ROLES).find((role) => roles?.includes(role))
  const mappedIcd10s =
    (icd10s?.all || []).map((obj: any) => ({
      label: `${obj.code} ${obj.desc}`,
      value: obj.value,
    })) || []
  const worksheets = contents
    .filter(({ group }: any) => group === 'exercise')
    .map(({ title }: any) => title)
    .sort()
  const lessons = contents
    .filter(({ group }: any) => group === 'lesson')
    .map(({ title }: any) => title)
    .sort()
  const infosheets = contents
    .filter(({ group }: any) => group === 'infosheet')
    .map(({ title }: any) => title)
    .sort()
  const treatmentFocusTitles = Object.entries(TREATMENT_FOCUS_TITLES)?.map(([treatmentValue, treatmentTitle]) => ({
    label: intl.formatMessage(treatmentTitle),
    value: treatmentValue,
  }))

  const notesThatNeedDeclaration = [
    'bctInitialNote',
    'bctSessionNote',
    'audSudExpansionInitialNote',
    'audSudExpansionSessionNote',
    'daInitialNote',
    'daSessionNote',
    'daGeneralNote',
    'dbtInitialNote',
    'dbttCombinedSessionNote',
    'dbttInitialNote',
    'dbtSessionNote',
    'cgtInitialNote',
    'cgtSessionNote',
  ]
  const isSupervisor = !!supervisor && !isEmpty(supervisor.roles) && hasRole(supervisor.roles, SUPERVISOR_ROLES)
  const isFellowSupervisor =
    supervisor &&
    !isEmpty(supervisor.roles) &&
    (hasRole(supervisor.roles, ROLES.LT_SUPERVISOR) || hasRole(supervisor.roles, ROLES.LT_FELLOW_SUPERVISOR))
  const isFellow = hasRole(roles, ROLES.LT_FELLOW_THERAPIST)

  const resolvedProviderName = isFellowSupervisor ? `${supervisor.first_name} ${supervisor.last_name}` : userName

  const customerPropertyId = episode?.program_name ? PROGRAMS[episode.program_name]?.customerPropertyId : undefined

  const isProgramEAPSupported = programSupportForAttribute('eapSupported', episode, programCoverageBreakdown)

  const sessionLimitByProgram = clientDetails?.sessions_limit_by_program
  const sessionsUsedByProgram = clientDetails?.sessions_used_by_program
  const openBhbSessionCount = clientDetails?.open_bhb_session_count
  const specialityCareSessionCount = clientDetails?.speciality_care_session_count

  let isStillOnEAPSessions = true

  if (sessionLimitByProgram && customerPropertyId) {
    // TODO: Potential bug here: customerPropertyId is being used as directly as a TREATMENT_OPTIONS_TYPE
    const programType = getSessionLimitProgramCategoryByTreatmentOption(
      customerPropertyId as unknown as TREATMENT_OPTIONS_TYPE,
    )
    const sessionsLimit = sessionLimitByProgram[programType]
    const usedSessionCount = sessionsUsedByProgram?.[programType] ?? 0
    isStillOnEAPSessions =
      sessionsLimit === undefined ||
      isUnlimitedSessionLimit(sessionsLimit) ||
      usedSessionCount < sessionsLimit ||
      (usedSessionCount === sessionsLimit &&
        (openBhbSessionCount ?? 0) === 0 &&
        (specialityCareSessionCount ?? 0) === 0)
  } else {
    const eapSessionLimit = resolveSessionLimit(
      episode?.program_name,
      clientDetails,
      forceAdultFlowsToHPI,
      true, // isEAP
    )
    const eapSessionsUsed = clientDetails?.sessions_used ?? 0
    isStillOnEAPSessions =
      eapSessionLimit === undefined ||
      isUnlimitedSessionLimit(eapSessionLimit) ||
      eapSessionsUsed < eapSessionLimit ||
      (eapSessionsUsed === eapSessionLimit &&
        (openBhbSessionCount ?? 0) === 0 &&
        (specialityCareSessionCount ?? 0) === 0)
  }

  const getIsEAPSession = (): boolean => {
    if (noteIsCompleted && note.body?.isEAPSession !== undefined) {
      return Boolean(note.body.isEAPSession)
    }
    // Backward compatibility: old notes don't have isEAPSession stored,
    // but EAP notes always had primaryFocusForTreatment field
    if (noteIsCompleted && note.body?.primaryFocusForTreatment !== undefined) {
      return true
    }
    return Boolean(isProgramEAPSupported && isStillOnEAPSessions)
  }

  const isEAPSession = getIsEAPSession()

  const isAirlineCustomer =
    noteIsCompleted && note.body?.isAirlineCustomer !== undefined
      ? Boolean(note.body.isAirlineCustomer)
      : Boolean(
          clientDetails?.employer &&
          Array.isArray(airlineCustomerValues) &&
          airlineCustomerValues.includes(clientDetails?.employer?.toLowerCase()),
        )

  const clientCustomer = customers?.find((customer) => customer.value === clientDetails?.employer)
  const isASDCoordinationEnabled = clientCustomer?.is_asd_coordination_enabled === 'true'
  const isNeurodiversityCareAdvocacyEnabled = clientCustomer?.neurodiversity_care_advocacy === 'true'
  const isGenAiOptedOut =
    noteIsCompleted && note.body?.isGenAiOptedOut !== undefined
      ? Boolean(note.body.isGenAiOptedOut)
      : // An unresolved customer counts as opted out so we never ask for AI consent we shouldn't
        !clientCountryCustomer || clientCountryCustomer.gen_ai_opt_out === 'true'
  const isMGCAiEnabled =
    noteIsCompleted && note.body?.isMGCAiEnabled !== undefined
      ? Boolean(note.body.isMGCAiEnabled)
      : clientCountryCustomer?.is_mgc_ai_enabled === 'true'

  // List of provider referral types that are available in the referral note for all programs
  const providerReferralTypeOptions = [
    'Current program (but with a different provider)',
    'Coaching',
    'Individual Therapy',
    'Couples Therapy',
    'Family Therapy',
    'Medication Consultation',
    'Lyra DBT',
    'Lyra Renew (Substance Program)',
    'Employer Health Plan - client not eligible through Lyra',
    'Guided Self-Care with a Coach',
    'Coaching for Parents',
    'Clinical Leave Evaluations',
    ...(isASDCoordinationEnabled || isNeurodiversityCareAdvocacyEnabled ? ['Neurodiversity Navigation'] : []),
  ]

  const [isNoteBeingManuallySubmitted, setIsNoteBeingManuallySubmitted] = useState(false)
  const [isNoteBeingAutoSaved, setIsNoteBeingAutoSaved] = useState(false)
  const [autoSaveFailed, setAutoSaveFailed] = useState(false)
  const [closeSessionErrorMessage, setCloseSessionErrorMessage] = useState<string | null>(null)
  const [isPanelOpen, setIsPanelOpen] = useState(false)
  const [showHeader, setShowHeader] = useState(false)

  const isMounted = useIsMounted()

  const prevRoute = usePreviousRoute()

  const updatedNoteValuesRef = useRef<object>({})
  const asyncSaveForm = async ({ values }: { values: object }): Promise<void> => {
    setIsNoteBeingAutoSaved(true)
    await submitForm(values, 'draft')
  }

  const {
    canLoadPreviousNote,
    handleLoadPreviousNote,
    handleFlexibleNoteFormatChange,
    previousNoteFormat,
    isLoadingPreviousNoteFormat,
  } = useLoadPreviousFlexibleNote({
    noteRef,
    note,
    episodeId: episode?.id,
    appointments,
    isFlexibleNote,
    noteIsCompleted,
  })

  const didHPEligibilityAPIFail = isFlexibleNote && closeSessionEligibility.didHPEligibilityAPIFail

  // Centralized sticky offsets
  const headerTopOffset = NOTE_DETAILS_HEADER_HEIGHT
  const alertBannerTopOffset =
    (autoSaveFailed ? ALERT_BANNER_HEIGHT : 0) +
    (didHPEligibilityAPIFail ? ALERT_BANNER_HEIGHT : 0) +
    (closeSessionErrorMessage ? ALERT_BANNER_HEIGHT : 0)
  const joinSessionBannerTopOffset = hasJoinSessionBannerOpen ? JOIN_BANNER_HEIGHT : 0
  const videoSessionBannerHeight = useVideoSessionBannerHeight()
  const activeAlertTopOffset = hasActiveAlerts ? ACTIVE_ALERT_HEIGHT : 0
  const combinedTopOffset = headerTopOffset + alertBannerTopOffset
  const sidebarTopOffset = headerTopOffset + 32 + alertBannerTopOffset

  const [saveForm] = useThrottledCallback(({ values, formSubmit }: { values: object; formSubmit: boolean }): void => {
    if (formSubmit) {
      setIsNoteBeingManuallySubmitted(true)
    }
    if (isNoteBeingAutoSaved) {
      return
    }
    !formSubmit && setIsNoteBeingAutoSaved(true)
    isMounted() && submitForm(values, formSubmit ? 'completed' : 'draft')
  }, 5000)

  const submitForm = async (values: any, status: any) => {
    // For slower chromebook machines, sometimes the autosave will trigger before the note values are initialized
    if (isEmpty(values)) {
      setIsNoteBeingAutoSaved(false)
      return
    }

    setCloseSessionErrorMessage(null)

    const nextAppointment = note.appointment_id
      ? (getFollowingAppointment(note.appointment_id, appointments) ?? null)
      : null

    const body = {
      ...values,
      approverName: isFellow && status === 'completed' ? `${supervisor?.first_name} ${supervisor?.last_name}` : null,
      approverLyraId: isFellow && status === 'completed' ? supervisor?.id : null,
      approverRole: isFellow && status === 'completed' ? supervisor?.roles : null,
      authorName: isFellow ? userName : isSupervisor ? `${supervisor.first_name} ${supervisor.last_name}` : userName,
      authorLyraId: isFellow ? userId : isSupervisor ? supervisor.id : userId,
      authorRoles: isFellow ? roles : isSupervisor ? supervisor.roles : roles,
      providerType: isSupervisor ? supervisor.role : providerType,
      isEAPSession,
      isAirlineCustomer,
      isGenAiOptedOut,
      isMGCAiEnabled,
    }

    // Unified flow: close the session before saving the note (no-op for non-flexible notes/drafts).
    const {
      chargeId: flexibleNoteChargeId,
      aborted: closeSessionAborted,
      abortReason: closeSessionAbortReason,
      errorMessage: closeSessionServerErrorMessage,
      isPendingNoteCharge: flexibleNoteIsPendingNoteCharge,
      chargeAttempted: flexibleNoteChargeAttempted,
      isCancellationLimitExceeded: flexibleNoteCancellationLimitExceeded,
    } = effectiveIsChargeAlreadySubmitted
      ? {
          chargeId: effectiveSubmittedChargeId ?? null,
          aborted: false,
          abortReason: undefined,
          errorMessage: undefined,
          // The already-submitted charge was found by querying for PENDING_NOTE transfers, so it is
          // pending_note by definition and still needs the accept-charge step.
          isPendingNoteCharge: true,
          chargeAttempted: true,
          isCancellationLimitExceeded: false,
        }
      : await closeSession({
          values,
          status,
          noteId: note.id,
        })
    if (closeSessionAborted) {
      if (closeSessionAbortReason === 'missingNoteId') {
        dispatch(
          setToastContent({
            text: intl.formatMessage({
              defaultMessage: "Your note hasn't finished saving yet. Please wait a moment and try again.",
              description: 'Error shown when submitting before the note has been saved as a draft',
            }),
            id: 'ClientNoteDetails-close-session-aborted',
            toastType: 'error',
          }),
        )
      } else {
        setCloseSessionErrorMessage(
          closeSessionServerErrorMessage ||
            intl.formatMessage({
              defaultMessage: 'There was an error closing the session. Please try again.',
              description: 'Error shown when closing the session fails so the note was not submitted',
            }),
        )
      }
      setIsNoteBeingManuallySubmitted(false)
      return
    }

    if (flexibleNoteChargeId && !effectiveIsChargeAlreadySubmitted) {
      sessionChargeIdRef.current = flexibleNoteChargeId
    }

    let res: Note | null = null
    const episodeIdForSave = note.id ? (note.episode_id ?? episode?.id) : episode?.id
    const metadataToSave = isFlexibleNote ? scrubFlexibleNotePII(noteMetadata) : noteMetadata
    const data = {
      content_meta_data_id: noteMetadata.id,
      content_meta_data: metadataToSave,
      patient_id: clientId,
      provider_id: userId,
      episode_id: episodeIdForSave,
      appointment_id: note.appointment_id,
      note_type: note.note_type,
      version_id: note.version_id,
      body: body,
      status,
      next_appointment_id: status === 'completed' && nextAppointment ? nextAppointment.appointmentId : null,
      next_appointment_start_datetime:
        status === 'completed' && nextAppointment ? getDateFromAppointment(nextAppointment).toISOString() : null,
    }

    if (note.id) {
      try {
        setAutoSaveFailed(false)
        res = await updateNote({ note_id: note.id, ...data }).unwrap()
      } catch (error) {
        logToSentry('noteSubmissionError', {
          error: error instanceof Error ? { message: error.message, stack: error.stack } : { error },
          appointmentId: note.appointment_id,
          episodeId: episodeIdForSave,
          contextEpisodeId: episode?.id,
          noteEpisodeId: note.episode_id,
          clientId,
        })

        setAutoSaveFailed(true)
      }
    } else {
      try {
        setAutoSaveFailed(false)
        res = await createNote(data).unwrap()
        setIsNoteBeingManuallySubmitted(false)
      } catch (error) {
        logToSentry('noteSubmissionError', {
          error: error instanceof Error ? { message: error.message, stack: error.stack } : { error },
          appointmentId: note.appointment_id,
          episodeId: episodeIdForSave,
          contextEpisodeId: episode?.id,
          noteEpisodeId: note.episode_id,
          clientId,
        })

        setAutoSaveFailed(true)
      }
    }

    if (!res && status === 'completed') {
      setIsNoteBeingManuallySubmitted(false)
      return
    }

    let invoiceFailedState:
      | {
          daInvoiceFailedModal: {
            show: boolean
            clientName: string
            sessionNumber: string | number
          }
        }
      | undefined

    // We only want to process the payment for DA providers after the session note has been completed.
    // To control this we set the status of the charge to pending_note when the session is closed.
    // After the note is completed, we will update the charge status to pending - by calling updateTransfer with
    // accept status
    const effectiveChargeId = isFlexibleNote ? (flexibleNoteChargeId ?? chargeIdToUpdate) : chargeIdToUpdate
    const shouldUpdateTransfer = isFlexibleNote ? !!(flexibleNoteChargeId ?? chargeIdToUpdate) : needToUpdateCharge
    const isPendingNoteCharge = isFlexibleNote ? flexibleNoteIsPendingNoteCharge : true
    const chargeAttempted = isFlexibleNote ? flexibleNoteChargeAttempted : needToUpdateCharge
    const showPaymentSuccessToast = () => {
      const sessionNumber = note.sessionNumber || (note?.body?.sessionNumber as string)
      const toastText = clientDetails
        ? `Session ${sessionNumber} closed for ${clientDetails.first_name} ${clientDetails.last_name}. Note submitted and payment is processing.`
        : `Session ${sessionNumber} closed. Note submitted and payment is processing.`
      dispatch(
        setToastContent({
          text: toastText,
          id: 'ClientNoteDetails-transfer-success',
          toastType: 'success',
        }),
      )
    }

    if (
      res?.id &&
      res?.status === 'completed' &&
      isEngageDAProvider &&
      effectiveChargeId &&
      clientId &&
      shouldUpdateTransfer &&
      isPendingNoteCharge
    ) {
      try {
        await updateTransfer({
          chargeId: effectiveChargeId,
          patient_lyra_id: clientId,
          status: 'accept',
        }).unwrap()

        showPaymentSuccessToast()
      } catch (err) {
        Sentry.addBreadcrumb({
          category: 'Payments',
          data: {
            chargeId: effectiveChargeId,
            patient_lyra_id: clientId,
          },
        })
        Sentry.captureException(err)
      }
    } else if (
      // Late-cancel/no-show charges are created with `pending` status (not `pending_note`),
      // so no updateTransfer accept step is needed — just show the success toast.
      res?.id &&
      res?.status === 'completed' &&
      isEngageDAProvider &&
      effectiveChargeId &&
      chargeAttempted &&
      !isPendingNoteCharge
    ) {
      showPaymentSuccessToast()
    } else if (
      res?.id &&
      res?.status === 'completed' &&
      isEngageDAProvider &&
      !effectiveChargeId &&
      chargeAttempted &&
      !flexibleNoteCancellationLimitExceeded &&
      accessType !== 'T0' &&
      (noteType === NoteTypes.SESSION || noteType === NoteTypes.INITIAL)
    ) {
      const sessionNumber = note.sessionNumber || (note?.body?.sessionNumber as string)
      const clientName = clientDetails?.full_name || ''

      logToSentry('daNotesWorkflow', {
        message: 'DA notes workflow: charge ID is missing',
        noteId: res.id,
        clientId,
        originRoute,
      })

      invoiceFailedState = {
        daInvoiceFailedModal: {
          show: true,
          clientName,
          sessionNumber,
        },
      }
    }

    if (res?.id && res?.status === 'completed') {
      track({
        event: mixpanelEvents.SUBMIT_NOTE,
        details: {
          entryPoint:
            originRoute === 'calendar'
              ? 'In Product Calendar'
              : isFromRiskAlerts
                ? 'Risk Alert Popover'
                : isFromClientList
                  ? 'Client List Risk Popover'
                  : isFromCareTimeline
                    ? 'Care Timeline'
                    : isFromClientOverview
                      ? 'Client Overview'
                      : 'Sessions tab',
          ...(note.appointment_id && { [S4C_PROPERTIES.APPOINTMENT_ID]: note.appointment_id.toString() }),
        },
      })
    }

    if (status === 'draft') {
      // when we save a draft, we filter out the Sign heading and Supervisor declaration because otherwise,
      //  the saved version can be edited by the Fellow, who can then check the Declaration checkbox
      if (isFellowSupervisor) {
        data.content_meta_data = removeContentMetadataField(data.content_meta_data, 'sign')
        data.content_meta_data = removeContentMetadataField(data.content_meta_data, 'declareInfoAccurateAndComplete')
      }
      setIsNoteBeingAutoSaved(false)
      setNote({ ...note, ...res })
    } else {
      const navigateTo = getOriginRouteDestination({ originRoute, isEngageDAProvider })
      if (invoiceFailedState) {
        navigate(navigateTo, { state: invoiceFailedState })
      } else {
        navigate(navigateTo)
      }
    }
    return res
  }

  const noteDeclaration = contents.find((n: any) => n.name === 'noteDeclaration')
  const noteDeclarationSupervisor = contents.find((n: any) => n.name === 'noteDeclarationSupervisor')
  const fellowQuestions = contents.find((n: any) => n.name === 'fellowQuestions')

  const noteHasDeclarationCheckbox =
    note?.content_meta_data?.meta_data?.schema?.properties?.declareInfoAccurateAndComplete
  const noteHasFellowDisclosure = note?.content_meta_data?.meta_data?.schema?.properties?.fellowDisclosure

  if (isFellowSupervisor && !noteHasDeclarationCheckbox && note.status !== 'completed' && noteDeclarationSupervisor) {
    note.content_meta_data = extendContentMetadata(note.content_meta_data, noteDeclarationSupervisor)
    noteMetadata = note.content_meta_data
  } else if (
    note?.content_meta_data?.name &&
    notesThatNeedDeclaration.includes(note.content_meta_data.name) &&
    !isFellow &&
    !noteHasDeclarationCheckbox &&
    note.status !== 'completed' &&
    noteDeclaration
  ) {
    note.content_meta_data = extendContentMetadata(note.content_meta_data, noteDeclaration)
    noteMetadata = note.content_meta_data
  }

  if (
    isFellow &&
    !noteHasFellowDisclosure &&
    note?.content_meta_data?.name === 'bctInitialNote' &&
    note.status !== 'completed' &&
    fellowQuestions
  ) {
    // supply an optional flag to extendMetadata to set the order of the added metadata to be above (defaults to below) the original
    note.content_meta_data = extendContentMetadata(note.content_meta_data, fellowQuestions, true)
    // Keep noteMetadata in sync (like the declaration blocks above) — otherwise the
    // `note.content_meta_data = noteMetadata` reassignment after useFlexibleNoteMetadata wipes fellowDisclosure.
    noteMetadata = note.content_meta_data
  }

  if (
    episode?.program_name == ProgramNames.CoachingForParents &&
    note.content_meta_data.meta_data.schema?.properties?.isCoachingForParentsEpisode &&
    note.content_meta_data.meta_data.uiSchema?.isCoachingForParentsEpisode
  ) {
    // If CoachingForParents program, disable isCoachingForParentsEpisode question and set value to true
    note.content_meta_data = {
      ...note.content_meta_data,
      meta_data: {
        ...note.content_meta_data.meta_data,
        initialValues: {
          ...note.content_meta_data.meta_data.initialValues,
          isCoachingForParentsEpisode: 1,
        },
        uiSchema: {
          ...note.content_meta_data.meta_data.uiSchema,
          isCoachingForParentsEpisode: {
            ...note.content_meta_data.meta_data.uiSchema.isCoachingForParentsEpisode,
            'ui:disabled': true,
            'ui:readonly': true,
          },
        },
      },
    }
    // Keep noteMetadata in sync so the post-useFlexibleNoteMetadata reassignment doesn't drop these edits.
    noteMetadata = note.content_meta_data
    if (!!note.body && Object.prototype.hasOwnProperty.call(note.body, 'isCoachingForParentsEpisode')) {
      note.body = {
        ...note.body,
        isCoachingForParentsEpisode: 1,
      }
    }
  }

  // DA Flexible Note: source the shared close-session fields (modality, reference #, rate, conditions)
  // from the same metadata the end-of-session screen uses, grafted into the note by ui:order.
  noteMetadata = useFlexibleNoteMetadata({
    baseMetadata: noteMetadata,
    isFlexibleNote,
    isEAPSession,
    appointmentToClose: noteAppointment,
    selectedEpisode: episode,
    eligibility: closeSessionEligibility,
    chargeAlreadySubmitted: effectiveIsChargeAlreadySubmitted,
    noteIsCompleted,
    noteBody: note.body,
  })

  const reversedChargeExternalValues = useMemo(
    () => ({
      isEAPSession,
      isAirlineCustomer,
      showHealthPlanIcd10,
      isGenAiOptedOut,
      isMGCAiEnabled,
    }),
    [isEAPSession, isAirlineCustomer, showHealthPlanIcd10, isGenAiOptedOut, isMGCAiEnabled],
  )

  // Re-lock here, not via the effect below: useFlexibleNoteMetadata regrafts close-session fields each render.
  // Memoised so the cloneDeep inside getReversedChargeNoteMetadata only runs when inputs change.
  noteMetadata = useMemo(() => {
    if (isFlexibleNote && isReversedChargeNote) {
      return getReversedChargeNoteMetadata(noteMetadata, {
        noteValues: note.body,
        formatMessage: intl.formatMessage,
        externalValues: reversedChargeExternalValues,
        editableFieldNames: DA_FLEXIBLE_NOTE_REVERSED_CHARGE_EDITABLE_FIELDS,
      })
    }
    return noteMetadata
  }, [noteMetadata, isFlexibleNote, isReversedChargeNote, note.body, intl.formatMessage, reversedChargeExternalValues])
  note.content_meta_data = noteMetadata

  const noteForForm = useMemo(() => {
    if (!isFlexibleNote || noteIsCompleted) {
      return note
    }
    const appointmentProgramName = getProgramNameFromId(noteAppointment?.programId)
    const therapyType = appointmentProgramName ? PROGRAM_TO_THERAPY_TYPE[appointmentProgramName] : undefined

    const seededBody = getSeededFlexibleNoteBody({
      body: note.body,
      previousNoteFormat,
      therapyType,
      resolvedProviderName: isFellowSupervisor
        ? resolvedProviderName
        : noteAppointment?.provider?.display_name || resolvedProviderName,
    })
    if (isEmpty(seededBody)) {
      return note
    }
    return {
      ...note,
      content_meta_data: noteMetadata,
      body: {
        ...note.body,
        ...seededBody,
      },
    }
  }, [
    note,
    noteMetadata,
    isFlexibleNote,
    noteIsCompleted,
    previousNoteFormat,
    noteAppointment,
    resolvedProviderName,
    isFellowSupervisor,
  ])

  // Standard DA notes lock once via this effect; the flexible note transforms in-render (above).
  // For reversed charge DA notes, set fields to read-only and add addendum field.
  // fields that are required and were previously not entered or had invalid values, will remain editable
  useEffect(() => {
    if (
      isReversedChargeNote &&
      isDASessionNoteType(note?.content_meta_data?.name) &&
      !hasLockedReversedChargeFields.current
    ) {
      hasLockedReversedChargeFields.current = true
      setNote((prevNote) => {
        const newNote = getReversedChargeNoteMetadata(prevNote.content_meta_data, {
          noteValues: prevNote.body,
          formatMessage: intl.formatMessage,
          externalValues: reversedChargeExternalValues,
        })
        return {
          ...prevNote,
          content_meta_data: newNote,
        }
      })
      setIsReversedChargeMetadataTransformed(true)
    }
    // The ref guard makes the transform run at most once. We depend on isReversedChargeNote
    // and the note name so the effect fires once the reversed charge is detected (the
    // transfers query resolves async). The remaining deps (intl + EAP/airline flags) are
    // only read inside and listed to satisfy react-hooks/exhaustive-deps; they can't trigger
    // a re-run once the guard is set.
  }, [isReversedChargeNote, note?.content_meta_data?.name, intl, reversedChargeExternalValues])

  const appointment = noteAppointment
  const isNoteForFutureAppointment = Boolean(appointment && getDateFromAppointment(appointment) > new Date())
  const isNpEpisode = isNeuropsychEvaluationProgram(episode?.program_name)
  // A signed note keeps the age it had at the session: fall back to its sign date when the appointment is not loaded.
  const npSessionDate = appointment
    ? getDateFromAppointment(appointment)
    : note.submit_date
      ? new Date(note.submit_date)
      : undefined
  const npSessionTime = npSessionDate?.getTime()
  const npExternalValues = useMemo(
    () =>
      isNpEpisode
        ? getNpSessionNoteExternalValues(
            clientDetails?.date_of_birth,
            npSessionTime ? new Date(npSessionTime) : undefined,
            roles,
            noteIsCompleted,
          )
        : undefined,
    [isNpEpisode, clientDetails?.date_of_birth, npSessionTime, roles, noteIsCompleted],
  )
  // The NP session note has the questions of the chosen session type only: a type without questions cannot be signed.
  const [npAnsweredSessionType, setNpAnsweredSessionType] = useState<string>()
  const npSessionType = (npAnsweredSessionType ?? note.body?.sessionType) as string | undefined
  const isNpFormUnavailable =
    isNpEpisode &&
    note.content_meta_data?.name === NP_SESSION_NOTE_NAME &&
    (!NP_SESSION_TYPES_WITH_FORM.includes(npSessionType ?? '') ||
      !hasNpTemplateQuestions(note.content_meta_data, npSessionType ?? ''))
  const showNpFormUnavailableWarning = isNpFormUnavailable && !!npSessionType
  // The submit button is disabled for every future appointment; only NP (behind its flag) gets the explanation banner.
  const isNpTestingNote = !!isNPTestingEnabled && isNeuropsychEvaluationProgram(episode?.program_name)
  const showFutureAppointmentWarning = isNpTestingNote && isNoteForFutureAppointment && !noteIsCompleted
  const daSessionOlderThan90Days = isFlexibleNote && isSessionOlderThan90Days(appointment)
  const programName = getProgramNameFromId(episode?.program_id) ?? ''
  const noteAppointmentSummary: AppointmentSummary | undefined = useMemo(() => {
    return showAINotesFeatures
      ? programConfig[programName as LTProgramName]?.aiNotesEnabled &&
        (programName !== ProgramNames.MedicationManagement ||
          (programName === ProgramNames.MedicationManagement && showAINotesForLCMAndCLE)) &&
        (programName !== ProgramNames.ClinicalLeaveEvaluation ||
          (programName === ProgramNames.ClinicalLeaveEvaluation && showAINotesForLCMAndCLE)) &&
        (programName !== ProgramNames.SubstanceUseEvaluation ||
          (programName === ProgramNames.SubstanceUseEvaluation && showAINotesForLCMAndCLE))
        ? appointmentSummaries?.find(
            (summary: AppointmentSummary) => summary.appointment_id === note.appointment_id?.toString(),
          )
        : undefined
      : undefined
  }, [appointmentSummaries, note.appointment_id, programName, showAINotesFeatures, showAINotesForLCMAndCLE])

  const firstAIEnabledField = useMemo(() => {
    const schemaProperties = noteMetadata?.meta_data?.schema?.properties as unknown as SchemaProperties
    if (!schemaProperties) {
      return
    }

    return getAIEnabledFields({ note: noteMetadata, fields: schemaProperties, noteValues: note.body })[0]
  }, [noteMetadata, note.body])

  const contentProgram: Program | undefined = contentsPrograms.find((program) => program.name === programName)

  const promptType =
    noteType === NoteTypes.INITIAL && contentProgram?.appt_initial_ai_prompts
      ? contentProgram?.appt_initial_ai_prompts?.[0]
      : NoteTypes.SESSION && contentProgram?.appt_follow_up_ai_prompts
        ? contentProgram?.appt_follow_up_ai_prompts?.[0]
        : undefined

  const displayAINotesPanel =
    showAINotesFeatures &&
    hasFetchedSummaries &&
    noteAppointmentSummary &&
    ([AIDraftStatus.IN_PROGRESS, AIDraftStatus.NOT_STARTED].includes(noteAppointmentSummary.status) ||
      (noteAppointmentSummary.status === AIDraftStatus.COMPLETE && noteAppointmentSummary?.summary?.length)) &&
    !isNoteForFutureAppointment &&
    note.status !== NoteStatusStates.COMPLETED

  const aiPanelProps = useMemo(() => {
    if (!noteAppointmentSummary || !firstAIEnabledField?.name || !promptType || !note?.appointment_id) {
      return undefined
    }
    return {
      feedbackFormRef,
      fieldName: firstAIEnabledField.name,
      noteAppointmentSummary,
      noteId: note.id,
      noteRef,
      panelTitle: firstAIEnabledField.title ?? '',
      noteType: note.note_type,
      appointmentId: note.appointment_id.toString(),
      promptType,
    }
  }, [
    feedbackFormRef,
    firstAIEnabledField?.name,
    firstAIEnabledField?.title,
    note?.appointment_id,
    note?.id,
    note?.note_type,
    noteRef,
    promptType,
    noteAppointmentSummary,
  ])
  const tabbedExpandingSidePanelContent = useGetTabbedExpandingSidePanelContent({
    showAISummary: Boolean(displayAINotesPanel),
    noteType,
    aiPanelProps,
  })

  const isNotesSidePanelEnabledForNoteType = Boolean(noteType && (notesSidePanel as any)?.[noteType as string])

  const shouldRunAIBehavior = useMemo(
    () =>
      !isNotesSidePanelEnabledForNoteType ||
      (isNotesSidePanelEnabledForNoteType && tabbedExpandingSidePanelContent.selectedTab === PANEL_TAB_IDS.AI_NOTES),
    [isNotesSidePanelEnabledForNoteType, tabbedExpandingSidePanelContent.selectedTab],
  )
  const getAIWorkspaceTrackingDetails = useCallback(
    () => ({
      ...(noteAppointmentSummary?.status && {
        summaryStatus: noteSummaryStatusText[noteAppointmentSummary?.status],
      }),
      noteType: sessionNoteTypes[note.note_type],
      ...(note.appointment_id && { appointmentId: note.appointment_id?.toString() }),
    }),
    [note.appointment_id, note.note_type, noteAppointmentSummary?.status],
  )

  // Track AI workspace open on mount (once)
  const hasTrackedAIOpenOnMountRef = useRef(false)
  useEffect(() => {
    const isAiPanelOpen = isNotesSidePanelEnabledForNoteType ? isTabbedExpandingSidePanelOpen : isPanelOpen
    if (
      shouldRunAIBehavior &&
      isAiPanelOpen &&
      displayAINotesPanel &&
      !!aiPanelProps &&
      !hasTrackedAIOpenOnMountRef.current
    ) {
      hasTrackedAIOpenOnMountRef.current = true
      track({
        event: mixpanelEvents.BUTTON_PRESS,
        action: actions.AI_DRAFTS_WORKSPACE_OPEN,
        details: getAIWorkspaceTrackingDetails(),
      })
    }
  }, [
    aiPanelProps,
    displayAINotesPanel,
    getAIWorkspaceTrackingDetails,
    isNotesSidePanelEnabledForNoteType,
    isPanelOpen,
    isTabbedExpandingSidePanelOpen,
    shouldRunAIBehavior,
  ])

  const onOpenPanel = () => {
    if (shouldRunAIBehavior) {
      const isAiPanelOpen = isPanelOpen || isTabbedExpandingSidePanelOpen
      if (isAiPanelOpen) {
        feedbackFormRef.current?.restart()
      }
      track({
        event: mixpanelEvents.BUTTON_PRESS,
        action: isAiPanelOpen ? actions.AI_DRAFTS_WORKSPACE_CLOSE : actions.AI_DRAFTS_WORKSPACE_OPEN,
        details: getAIWorkspaceTrackingDetails(),
      })
    }

    isNotesSidePanelEnabledForNoteType
      ? setIsTabbedExpandingSidePanelOpen((prevState) => !prevState)
      : setIsPanelOpen(!isPanelOpen)
  }

  const scrollToFirstErrorIfNeeded = useCallback((setTouched: FormButtonParams['setTouched']) => {
    const errors = setTouched()
    if (errors.length < 1) {
      return
    }
    setTimeout(() => {
      document
        ?.querySelector('[role=alert]')
        ?.parentElement?.parentElement?.scrollIntoView({ block: 'center', inline: 'nearest', behavior: 'smooth' })
    }, 100)
  }, [])

  const headerSubmitButton = useCallback(() => {
    const handleSubmit = noteRef?.current?.submit
    const loading = noteRef?.current?.getState().submitting
    const setTouched = noteRef?.current?.mutators.setTouched

    return (
      <PrimaryButton
        text={loading ? 'Saving' : 'Submit'}
        onPress={() => {
          if (setTouched && handleSubmit) {
            scrollToFirstErrorIfNeeded(setTouched)
            handleSubmit()
          }
        }}
        testID={tID('ClientNoteDetails-submit')}
        disabled={
          isNoteForFutureAppointment ||
          isAsyncTimeEntryLocked ||
          isNpFormUnavailable ||
          daSessionOlderThan90Days ||
          didHPEligibilityAPIFail ||
          note.status === 'completed' ||
          loading
        }
        loading={isNoteBeingManuallySubmitted}
      />
    )
  }, [
    isNoteBeingManuallySubmitted,
    isNoteForFutureAppointment,
    isAsyncTimeEntryLocked,
    isNpFormUnavailable,
    daSessionOlderThan90Days,
    didHPEligibilityAPIFail,
    note.status,
    scrollToFirstErrorIfNeeded,
  ])

  const handleCancel = () => {
    if (isFromRiskAlerts || isFromDashboardTasks) {
      navigateToDashboard()
      return
    }
    if (isFromClientList) {
      navigate(LT_CLIENTS.route)
      return
    }
    if (isFromCareTimeline) {
      navigate(CLIENTS_SESSIONS.route)
      return
    }
    if (isFromClientOverview) {
      navigate(CLIENT_HOME.route)
      return
    }
    // if the previous route was close session then we want to nav back to the calendar since that
    // is the only flow to trigger the sequence of close session -> session note.
    if (
      prevRoute &&
      (CLIENT_END_OF_SESSION.route.includes(prevRoute) || LC_CALENDAR_CURRENT.route.includes(prevRoute))
    ) {
      navigate(LC_CALENDAR_CURRENT.route)
      return
    }
    navigate(CLIENT_NOTES.route)
  }

  const getDetailsContent = ({ hideBackNavigation }: { hideBackNavigation: boolean }) => {
    return (
      <View>
        <SubmissionAlertContainer topOffset={headerTopOffset}>
          {autoSaveFailed && (
            <AlertBanner
              dismissHandler={() => setAutoSaveFailed(false)}
              includeX={true}
              key='alertAutoSaveFailed'
              show={true}
              style={AlertBannerTypes.WARNING}
              contents={`We're having trouble saving`}
              minHeight={ALERT_BANNER_HEIGHT.toString()}
              icon={<AttentionIcon size={20} />}
              trailingLink={
                <Link
                  text='Try again'
                  color={colors.components.alertBanner.warning.text}
                  onPress={() => {
                    asyncSaveForm({ values: updatedNoteValuesRef.current })
                  }}
                  underline
                />
              }
            />
          )}
          {didHPEligibilityAPIFail && (
            <AlertBanner
              key='alertHPEligibilityFailed'
              testID={tID('ClientNoteDetails-HPEligibilityFailedBanner')}
              show={true}
              style={AlertBannerTypes.DANGER}
              contents={
                'Unable to verify health plan eligibility. Please refresh the page and try again. If this error persists, contact support.'
              }
              minHeight={ALERT_BANNER_HEIGHT.toString()}
            />
          )}
          {closeSessionErrorMessage && (
            <AlertBanner
              dismissHandler={() => setCloseSessionErrorMessage(null)}
              includeX={true}
              key='alertCloseSessionError'
              testID={tID('ClientNoteDetails-closeSessionErrorBanner')}
              show={true}
              style={AlertBannerTypes.DANGER}
              contents={closeSessionErrorMessage}
              minHeight={ALERT_BANNER_HEIGHT.toString()}
            />
          )}
        </SubmissionAlertContainer>
        <ContentContainer testID={tID('ClientNoteDetails-container')} offset={alertBannerTopOffset}>
          <MainSectionContainer>
            <CenteredContentContainer>
              <FlexSpacer grow={1} transitionTime={SIDEBAR_TRANSITION_TIME} />
              <ContentGroup>
                {!hideBackNavigation && (
                  <GoBackContainer
                    onPress={() => {
                      if (isFromRiskAlerts || isFromDashboardTasks) {
                        navigateToDashboard()
                      } else if (isFromClientList) {
                        navigate(LT_CLIENTS.route)
                      } else if (isFromCareTimeline) {
                        navigate(CLIENTS_SESSIONS.route)
                      } else if (isFromClientOverview) {
                        navigate(CLIENT_HOME.route)
                      } else {
                        navigate(CLIENT_NOTES.route, {
                          state: { selectedEpisode: get(location.state, 'selectedEpisode') },
                        })
                      }
                    }}
                    testID={tID('ClientNoteDetails-go-back')}
                  >
                    <ChevronIcon direction={ChevronIconDirection.LEFT} fillColor={colors.iconDefault} />
                    <BodyText text='Back' color={colors.textSecondary} />
                  </GoBackContainer>
                )}
                {noteType !== NoteTypes.GENERAL && (
                  <SidebarContainer topOffset={sidebarTopOffset}>
                    <NotesSidebar
                      sessionDate={note.sessionDate || (note?.body?.sessionDate as string)}
                      sessionNumber={note.sessionNumber || (note?.body?.sessionNumber as string)}
                      noteMetadata={noteMetadata}
                      completedNote={note.status === NoteStatusStates.COMPLETED}
                      noteValues={note.body}
                      summaryStatus={noteAppointmentSummary?.status}
                      clientDetails={!isNotesSidePanelEnabledForNoteType ? clientDetails : null}
                      externalValues={{ ...reversedChargeExternalValues, ...npExternalValues }}
                    />
                  </SidebarContainer>
                )}
                <NoteLayoutContainer>
                  {daSessionOlderThan90Days && (
                    <InlineWarningBanner
                      text={
                        <BodyText
                          text='Sessions older than 90 days cannot be submitted.'
                          size={BodyTextSize.DEFAULT}
                          color={colors.textWarning}
                        />
                      }
                      testId={tID('ClientNoteDetails-SessionOlderThan90DaysWarning')}
                    />
                  )}
                  {showFutureAppointmentWarning && (
                    <InlineWarningBanner
                      text={intl.formatMessage({
                        defaultMessage: 'This note cannot be signed until the scheduled start of the session',
                        description:
                          'Warning banner shown on a draft note whose appointment has not started yet, because the submit button is disabled until then',
                      })}
                      testId={tID('ClientNoteDetails-FutureAppointmentWarning')}
                    />
                  )}
                  {showNpFormUnavailableWarning && (
                    <InlineWarningBanner
                      text={intl.formatMessage({
                        defaultMessage: 'This session type does not have a form yet, so the note cannot be signed',
                        description:
                          'Warning banner shown on an NP session note when the chosen session type has no form available yet',
                      })}
                      testId={tID('ClientNoteDetails-NpFormUnavailableWarning')}
                    />
                  )}
                  {isFlexibleNote && eligibilityError && <EligibilityErrorBanner />}
                  {isReversedChargeNote &&
                    (isFlexibleNote || isDASessionNoteType(note?.content_meta_data?.name)) &&
                    note?.status !== NoteStatusStates.COMPLETED && (
                      <InlineWarningBanner
                        testId='ClientNoteDetails-reversedChargeWarning'
                        text={intl.formatMessage({
                          defaultMessage:
                            'Addendum Required: Include a reason for the resubmission and any necessary corrections, clarifications or additional information in the space below.',
                          description:
                            'Warning banner text shown when a provider needs to update a locked note with a reversed charge',
                        })}
                      />
                    )}
                  {noteIsCompleted && sessionNeedsClosing && (
                    <InlineInfoBanner
                      testId={tID('ClientNoteDetails-noChargeCloseSessionBanner')}
                      text={intl.formatMessage(
                        {
                          defaultMessage:
                            "This session note has already been submitted and can't be edited. However, no charge was created for this session. Click <link>here</link> to close the session in order to complete billing.",
                          description:
                            'Info banner shown on a submitted DA session note whose session was never closed, linking to the close session form',
                        },
                        {
                          link: (chunks: string) => (
                            <Link
                              text={chunks}
                              onPress={goToCloseSessionForCompletedNote}
                              underline
                              size={BodyTextSize.DEFAULT}
                              testID={tID('ClientNoteDetails-noChargeCloseSessionLink')}
                            />
                          ),
                        },
                      )}
                    />
                  )}
                  {isFlexibleNote && (
                    <SessionLimitWarnings {...sessionLimitInfo} testIDPrefix='ClientNoteDetails' section='warning' />
                  )}
                  {isAsyncTimeEntryLocked && (
                    <InlineWarningBanner
                      testId='ClientNoteDetails-asyncTimeEntryLockedWarning'
                      text={intl.formatMessage({
                        defaultMessage:
                          'Time entries can no longer be added or edited because this evaluation has been closed.',
                        description:
                          'Warning banner shown on an Async Time Note when its episode is no longer in progress',
                      })}
                    />
                  )}
                  <NoteContainer
                    readOnly={isAsyncTimeEntryLocked}
                    note={noteForForm}
                    formContext={{
                      intl,
                      icd10s: mappedIcd10s,
                      states: STATES.map((state) => state.value),
                      worksheets,
                      lessons,
                      infosheets,
                      treatmentFocusTitles,
                      dsmFifthEditionDiagnoses,
                      providerReferralTypeOptions,
                      externalValues: {
                        ...reversedChargeExternalValues,
                        ...getAsyncTimeNoteExternalValues(roles),
                        ...npExternalValues,
                      },
                      fieldProps: {
                        noteFormat:
                          canLoadPreviousNote && !isReversedChargeNote
                            ? {
                                ctaLabel: intl.formatMessage({
                                  defaultMessage: 'Load previous note',
                                  description: 'Link to load the previous session note into the current note',
                                }),
                                onCtaPress: handleLoadPreviousNote,
                              }
                            : undefined,
                      },
                    }}
                    saveForm={saveForm}
                    noteRef={noteRef}
                    onFormReady={() => setShowHeader(true)}
                    providerName={resolvedProviderName}
                    onNoteValuesChange={(values) => {
                      updatedNoteValuesRef.current = values
                      onFlexNoteValuesChange(values)
                      handleFlexibleNoteFormatChange(values?.noteFormat as string | undefined)
                      if (isNpEpisode) {
                        setNpAnsweredSessionType(values?.sessionType as string | undefined)
                      }
                    }}
                    reinitializeOnFieldsChange={isFlexibleNote ? FLEX_NOTE_RATE_FIELDS : undefined}
                  />
                  {isFlexibleNote && (
                    <SessionLimitWarnings {...sessionLimitInfo} testIDPrefix='ClientNoteDetails' section='limitInfo' />
                  )}
                </NoteLayoutContainer>
              </ContentGroup>
              <FlexSpacer grow={1} transitionTime={SIDEBAR_TRANSITION_TIME} />
            </CenteredContentContainer>
            {/* AI Notes Panel */}
            {displayAINotesPanel && !isNotesSidePanelEnabledForNoteType && (
              <AINotesPanelContainer
                testID={tID('ClientNoteDetails-aiNotesPanelContainer')}
                topOffset={combinedTopOffset}
              >
                <ExpandingSidePanel isPanelOpen={isPanelOpen} setIsPanelOpen={onOpenPanel}>
                  {isPanelOpen && note.appointment_id ? (
                    <ClientNotesPanel
                      feedbackFormRef={feedbackFormRef}
                      fieldName={aiPanelProps!.fieldName}
                      noteAppointmentSummary={noteAppointmentSummary!}
                      noteId={note.id}
                      noteRef={noteRef}
                      panelTitle={aiPanelProps!.panelTitle}
                      noteType={note.note_type}
                      appointmentId={note.appointment_id.toString()}
                      promptType={aiPanelProps!.promptType}
                    />
                  ) : (
                    <></>
                  )}
                </ExpandingSidePanel>
              </AINotesPanelContainer>
            )}
          </MainSectionContainer>
        </ContentContainer>
      </View>
    )
  }

  const isLoadingChargeChecks =
    isLoadingReversedChargeCheck || isLoadingChargeSubmittedCheck || isLoadingPreviousNoteFormat

  const isFlexNoteAwaitingAppointment =
    isFlexibleNote && !noteIsCompleted && !noteAppointment && (isLoadingAppointments || isAppointmentsUninitialized)
  const isReversedChargeNotePendingTransform =
    isReversedChargeNote && isDASessionNoteType(note?.content_meta_data?.name) && !isReversedChargeMetadataTransformed

  if (isLoadingChargeChecks || isFlexNoteAwaitingAppointment || isReversedChargeNotePendingTransform) {
    return <LoadingIndicator size={45} />
  }

  return (
    <WholeContainer testID={tID('ClientNoteDetails-wholeContainer')}>
      <LeftSideContainer testID={tID('ClientNoteDetails-leftSideContainer')}>
        {showHeader && (
          <StickyHeaderContainer testID={tID('ClientNoteDetails-stickyHeaderContainer')}>
            <ClientDetailsHeader
              clientDetails={clientDetails ?? null}
              handleExit={handleCancel}
              noteIsBeingAutoSaved={isNoteBeingAutoSaved}
              noteStatus={note.status}
              noteType={noteType}
              sessionNumber={note.sessionNumber || (note?.body?.sessionNumber as string)}
              showSubmit={!isFellow || isSupervisor}
              submitButton={headerSubmitButton}
              submitDate={note.submit_date ?? null}
              updateDate={note.update_date}
              setIsTabbedExpandingSidePanelOpen={onOpenPanel}
              isSideBarIconVisible={!isTabbedExpandingSidePanelOpen}
              hasSidePanelToggle={isNotesSidePanelEnabledForNoteType}
              showHipaaSecuredBadge={isFlexibleNote}
              {...(isFlexibleNote && {
                headerTextOverride: intl.formatMessage(
                  {
                    defaultMessage: 'Session invoice for {fullName}',
                    description: 'Header title for a DA Flexible Session Note',
                  },
                  { fullName: getClientPreferredNameIfExists(clientDetails) || clientDetails?.full_name },
                ),
                secondaryTextOverride: (() => {
                  const apptDate = noteAppointment
                    ? intl.formatDate(getDateFromAppointment(noteAppointment), {
                        weekday: 'long',
                        month: 'short',
                        day: 'numeric',
                        hour: 'numeric',
                        minute: '2-digit',
                        timeZoneName: 'short',
                        timeZone: noteAppointment.timeZone,
                      })
                    : ''
                  const saveState = noteIsCompleted
                    ? ''
                    : isNoteBeingAutoSaved
                      ? intl.formatMessage({
                          defaultMessage: 'Saving',
                          description: 'Indicator that the note is currently being saved',
                        })
                      : intl.formatMessage({
                          defaultMessage: 'All changes saved',
                          description: 'Indicator that all note changes have been saved',
                        })
                  const separator = apptDate && saveState ? ' \u00b7 ' : ''
                  return `${apptDate}${separator}${saveState}`
                })(),
              })}
            />
          </StickyHeaderContainer>
        )}
        {getDetailsContent({ hideBackNavigation: true })}
      </LeftSideContainer>
      {isNotesSidePanelEnabledForNoteType && (
        <RightSideContainer
          testID={tID('ClientNoteDetails-rightSideContainer')}
          offset={joinSessionBannerTopOffset + videoSessionBannerHeight}
        >
          <TabbedExpandingSidePanel
            isOpen={isTabbedExpandingSidePanelOpen}
            onClose={onOpenPanel}
            selectedTab={tabbedExpandingSidePanelContent.selectedTab}
            setSelectedTab={tabbedExpandingSidePanelContent.setSelectedTab}
            tabs={tabbedExpandingSidePanelContent.tabs}
            transitionTime={SIDEBAR_TRANSITION_TIME}
            contentHeightOffset={joinSessionBannerTopOffset + videoSessionBannerHeight + activeAlertTopOffset}
            hoveredTabId={tabbedExpandingSidePanelContent.hoveredTabId}
            setHoveredTabId={tabbedExpandingSidePanelContent.setHoveredTabId}
          />
        </RightSideContainer>
      )}
    </WholeContainer>
  )
}

export default ClientNoteDetails
